import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Per-route SEO head management for the SPA.
 *
 * The static index.html hardcodes canonical=https://newseze.com/ and a single
 * site-wide <title>. Googlebot renders JS, so without per-route overrides it
 * sees EVERY page claiming the homepage as canonical — and deduplicates the
 * whole site into one page. These two components fix that:
 *
 *  - <RouteSeo />  (mounted once in App.js) — sets canonical + og:url to the
 *    current route on every navigation, and restores the default title/
 *    description on pages that don't declare their own.
 *  - <SeoHead title description /> (mounted per page) — sets a unique
 *    <title> + meta description for content pages (articles, sections, etc.).
 */

const SITE = "https://newseze.com";
const DEFAULT_TITLE =
  "Newseze — News with Rewards · Balanced. Trusted. No drama. No conspiracies.";
const DEFAULT_DESC =
  "Newseze — balanced AI-curated news with rewards. No drama. No conspiracies. Earn credits, member benefits, and gift cards for every story you read.";

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/** Per-page title + description. Child effects run before the parent
 *  <RouteSeo /> effect, so the claim flag prevents the default reset. */
export function SeoHead({ title, description }) {
  const { pathname } = useLocation();
  useEffect(() => {
    if (title) {
      document.title = title.includes("Newseze") ? title : `${title} — Newseze`;
      upsertMeta("property", "og:title", document.title);
      window.__nzSeoClaim = pathname;
    }
    if (description) {
      const d = String(description).replace(/\s+/g, " ").trim().slice(0, 160);
      upsertMeta("name", "description", d);
      upsertMeta("property", "og:description", d);
    }
  }, [title, description, pathname]);
  return null;
}

/** Mounted once inside <BrowserRouter>. Canonical always follows the route. */
export function RouteSeo() {
  const { pathname } = useLocation();
  useEffect(() => {
    const canon = SITE + (pathname === "/" ? "/" : pathname.replace(/\/+$/, ""));
    upsertLink("canonical", canon);
    upsertMeta("property", "og:url", canon);
    if (window.__nzSeoClaim !== pathname) {
      document.title = DEFAULT_TITLE;
      upsertMeta("name", "description", DEFAULT_DESC);
      upsertMeta("property", "og:title", DEFAULT_TITLE);
      upsertMeta("property", "og:description", DEFAULT_DESC);
    }
  }, [pathname]);
  return null;
}
